import { createFileRoute } from '@tanstack/react-router';
import { ExternalUrls } from '../../lib/constants';
import { useSEO, useBreadcrumbSchema, useFAQSchema, PageSEO } from '../../lib/seo';
import { usePostHog } from '../../components/widgets/PostHogProvider';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Tabs,
  Tab,
  Link as MuiLink,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Section } from '../../components/ui/Section';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { LibraryMarquee } from '../../components/ui/LibraryMarquee';
import { CareCircle } from '../../components/ui/CareCircle';
import { FAQSection } from '../../components/sections/FAQSection';
import { CallToActionCard } from '../../components/ui/CallToActionCard';
import { FadeIn } from '../../components/ui/FadeIn';
import { BrandBackdrop } from '../../components/brand/BrandBackdrop';
import { InfoCard } from '../../components/ui/InfoCard';
import { FeatureIcon } from '../../components/ui/FeatureIcon';
import { MetricsSection } from '../../components/sections/MetricsSection';
import { ResourceCard } from '../../components/ui/ResourceCard';
import { TestimonialCarousel } from '../../components/ui/TestimonialCarousel';
import { ScreenshotShowcase } from '../../components/ui/ScreenshotShowcase';
import { NumberedStep } from '../../components/ui/NumberedStep';
import { getResourcesByType } from '../../lib/resources';
import { Testimonials } from '../../lib/social-proof';
import { NeutralColors, BrandColors } from '@neumind/shared/branding';
import { getFAQs } from '../../lib/faqs';

export const Route = createFileRoute('/solutions/professionals')({
  component: ProfessionalsPage,
});

const faqs = getFAQs('solution-professional');

const solutionCards = [
  {
    image: '/images/professionals/Adherence-tasks-only.webp',
    alt: 'A checklist with tasks including Complete your values worksheet and Practise leaving my house routine',
    title: 'Personalize Treatment at Scale',
    description:
      'Easily tailor interventions to each client\'s needs using a library of cognitive tools and strategies. neumind lets you deliver bespoke rehab plans without spending extra time developing materials for every client.',
  },
  {
    image: '/images/professionals/save-time-photo.webp',
    alt: 'Person in a light shirt writing with a blue pen near a stethoscope on a wooden table',
    title: 'Save Time on Admin',
    description:
      'Streamline repetitive tasks like scheduling, reminders, and note-tracking. Clinicians report saving over 30 minutes a week by reviewing in-app data instead of chasing updates by phone or email, allowing you to focus on client care.',
  },
  {
    image: '/images/professionals/medication-tracker.webp',
    alt: 'Medication Tracker dashboard showing 80% completion with checkmarks for morning medication',
    title: 'Real-Time Progress Tracking',
    description:
      'Get data-driven insights into each client\'s journey. Monitor adherence and improvements through the clinician dashboard, so you can adjust care plans proactively and demonstrate outcomes with confidence.',
  },
  {
    image: '/images/professionals/caregiver-hands.webp',
    alt: 'Two people shaking hands',
    title: 'Collaborate with Caregivers',
    description:
      'Connect family members and support workers through the platform to create a 360° support network. Everyone stays in the loop with the client\'s goals and progress, making transitions and home support more coordinated.',
  },
];

const evidenceCards = [
  {
    icon: <SchoolIcon />,
    title: 'Established Evidence Base',
    description:
      'neumind was designed to reflect established gold-standard cognitive-rehabilitation methods (reminders, structured tasks, journals and trackers) and was co-developed with neurorehab clinicians, individuals with brain injury and family members.',
    cta: { label: 'Evidence base', href: '/resources/research' },
  },
  {
    icon: <VerifiedIcon />,
    title: 'Recommended by INCOG Guidelines',
    description:
      'The International Cognitive Rehabilitation (INCOG) 2023 guidelines highlight smartphone-based external memory aids and "microprompting" as first-line strategies for severe memory impairment.',
    cta: { label: 'Key guidelines', href: '/resources/research' },
  },
  {
    icon: <FavoriteIcon />,
    title: 'Co-designed with Clients & Clinicians',
    description:
      'Born from lived experience and built with therapists, neumind has been iterated with NHS teams and client advisors to reduce cognitive load, simplify routines and keep families in the loop.',
    cta: { label: 'Co-design story', href: '/about' },
  },
  {
    icon: <AddCircleOutlineIcon />,
    title: 'Real-world Outcomes',
    description:
      'Early pilots and services report strong engagement when nudges, reminders and trackers are used, e.g. high medication adherence and appointment attendance, with results varying by context.',
    cta: { label: 'Read case study', href: '/resources/case-studies' },
  },
];

const privacyItems = [
  'GDPR compliant; PII separation & data compliance',
  'DPIA completed; processor agreements in place',
  'Regular security audits; privacy by design',
  'Clinical safety framework and risk management in place',
];

const timelineSteps = [
  {
    title: 'Create your account',
    description: 'Create your free account in minutes. Explore with a sample client and safely test features.',
  },
  {
    title: 'Add Clients',
    description: 'Draft client profiles, assign goals and therapies. Invite clients when ready.',
  },
  {
    title: 'Activate & Track',
    description:
      'Your client gets the neumind app preloaded with their plan (tools, goals and prompts) while you track and adjust.',
  },
];

function ProfessionalsPage() {
  useSEO(PageSEO.solutionsProfessionals);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Solutions', url: '/solutions' },
    { name: 'For Professionals', url: '/solutions/professionals' }
  ]);
  useFAQSchema(faqs);
  const { capture } = usePostHog();

  const [tabValue, setTabValue] = useState(0);
  const [scrollIndicatorVisible, setScrollIndicatorVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrollIndicatorVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollClick = () => {
    setScrollIndicatorVisible(false);
    window.scrollBy({ top: window.innerHeight * 0.7, behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, rgba(77, 101, 255, 0.05) 0%, rgba(14, 165, 223, 0.05) 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Brand-atmosphere layer: light cool mesh wash + a Care Circle
            constellation fading from the top-right. Pitched a touch more present
            than the clients hero — this is the clinician/MDT audience and the
            QR-code landing page for booth leads. Calibration per the brief:
            blue ≤10%, cyan ≤6%. */}
        <BrandBackdrop
          mesh="light"
          constellation
          constellationCorner="top-right"
          constellationOpacity={0.5}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              {/* ISS-260721-a0nj: this H1 previously reused the homepage hero
                  verbatim — a visitor arriving from the homepage read it as a
                  broken redirect. Professional-specific headline instead. */}
              <FadeIn direction="up" duration={800}>
                <Typography variant="h1" sx={{ mb: 3 }}>
                  See What Happens{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    Between Sessions
                  </Box>
                </Typography>
              </FadeIn>
              <FadeIn direction="up" delay={150} duration={800}>
                <Typography
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    fontSize: '1.25rem',
                    lineHeight: 1.7,
                  }}
                >
                  A digital neurorehabilitation platform for NHS and private teams that connects clinicians, clients and carers –
                  extending stroke, brain injury and cognitive rehab into daily life with real-time
                  monitoring and support.
                </Typography>
              </FadeIn>
              <FadeIn direction="up" delay={300} duration={800}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    component="a"
                    href={ExternalUrls.dashboardStart}
                    onClick={() =>
                      capture('cta_clicked', {
                        location: 'professionals_hero',
                        cta_label: 'Create Free Account',
                        cta_href: ExternalUrls.dashboardStart,
                        cta_type: 'primary',
                        cta_external: true,
                      })
                    }
                    sx={{ px: 4 }}
                  >
                    Create Free Account
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/contact-us"
                    onClick={() =>
                      capture('cta_clicked', {
                        location: 'professionals_hero',
                        cta_label: 'Book Demo',
                        cta_href: '/contact-us',
                        cta_type: 'secondary',
                        cta_external: false,
                      })
                    }
                    sx={{ px: 4 }}
                  >
                    Book Demo
                  </Button>
                </Stack>
                {/* Low-commitment CTA: links to the static sample-report
                    gallery (one client, five audience-tailored variants).
                    Hosted on www — no share.neumind.io dependency.
                    rpva item 4: the walkthrough found the sample report is
                    the hook that converts interest but was underplayed as a
                    body-text link — upgraded to a weighted card affordance. */}
                <MuiLink
                  href="/sample-reports/sarah/index.html"
                  target="_blank"
                  rel="noopener"
                  underline="none"
                  onClick={() =>
                    capture('cta_clicked', {
                      location: 'professionals_hero',
                      cta_label: 'Sample outcomes report',
                      cta_href: '/sample-reports/sarah/index.html',
                      cta_type: 'secondary',
                      cta_external: true,
                    })
                  }
                  sx={{
                    mt: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    maxWidth: 480,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: '1px solid rgba(27,58,107,0.10)',
                    boxShadow: '0 4px 12px -2px rgba(27,58,107,0.08)',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 12px 28px -6px rgba(27,58,107,0.16)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <FeatureIcon
                    icon={<AssessmentIcon />}
                    color={BrandColors.primary.main}
                    size="medium"
                    variant="square"
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                      See a sample outcomes report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The report-ready documentation neumind helps you produce
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
                </MuiLink>
              </FadeIn>
            </Grid>
            <Grid item xs={12} md={6}>
              <FadeIn direction="left" delay={200}>
                {/* Frame the clinician visual forward as a rounded editorial
                    panel: a soft primary orb-halo glows behind it for navy-tinted
                    elevation (Direction B signature), no invented product card. */}
                <Box sx={{ position: 'relative' }}>
                  <BrandBackdrop
                    motif="orb-halo"
                    motifCorner="top-right"
                    motifColor="37,99,235"
                    motifOpacity={0.16}
                    motifSize={520}
                  />
                  {/* VER-260729 walk: this hero previously shared
                      hero-tech-consulting.webp with /solutions/consulting —
                      swapped to the (previously unused) dashboard + app
                      composite so the two pages are differentiated and the
                      clinician audience sees the product surface they'd use.
                      Composite has its own card shadows/transparency, so no
                      frame radius/shadow here. */}
                  <Box
                    component="img"
                    src="/images/features/platform-overview.webp"
                    alt="neumind clinician dashboard beside the client app, showing a shared daily schedule"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    sizes="(max-width: 900px) 100vw, 560px"
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      width: '100%',
                      height: 'auto',
                    }}
                  />
                </Box>
              </FadeIn>
            </Grid>
          </Grid>
        </Container>

        {/* Scroll indicator */}
        {scrollIndicatorVisible && (
          <Box
            component="button"
            type="button"
            aria-label="Scroll down for more"
            onClick={handleScrollClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: 4,
              pb: 2,
              mx: 'auto',
              border: 'none',
              background: 'transparent',
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              opacity: 0.6,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Scroll for more
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                color: 'text.secondary',
                fontSize: 28,
                animation: 'bounce 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                  '40%': { transform: 'translateY(6px)' },
                  '60%': { transform: 'translateY(3px)' },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Bridge the Gap - Solution Section (moved up) */}
      <Section py={12}>
        <SectionHeader
          overline="Solution"
          title="Bridge the Gap"
          subtitle="Keep your clients engaged with therapeutic activities and routines between appointments, ensuring progress continues after they leave your clinic."
          centered
          sx={{ maxWidth: 700, mx: 'auto', mb: 6 }}
        />

        <Grid container spacing={4}>
          {solutionCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <FadeIn direction="up" delay={index * 100}>
                <Box
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={card.image}
                    alt={card.alt}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      mb: 2,
                    }}
                  />
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {card.description}
                  </Typography>
                </Box>
              </FadeIn>
            </Grid>
          ))}
        </Grid>

        <FadeIn direction="up" delay={400}>
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="outlined"
              component={Link}
              to="/resources/case-studies"
              endIcon={<span>→</span>}
            >
              Stories & Case Studies
            </Button>
          </Box>
        </FadeIn>
      </Section>

      {/* A Complete Digital Rehab Toolkit - Tabs Section */}
      <Box sx={{ bgcolor: NeutralColors[50], py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeader
            overline="Clinical planning meets everyday living"
            title="A Complete Digital Rehab Toolkit"
            subtitle="Manage your caseload with an intuitive dashboard. Easily add new clients, assign strategies with a few clicks, and get an overview of each client's app progress at a glance – all in one secure platform."
            centered
            sx={{ maxWidth: 700, mx: 'auto', mb: 6 }}
          />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, overflow: 'visible' }}>
            <Box sx={{ width: { xs: '100%', md: '40%' } }}>
              {/* Mobile: Horizontal scrollable tabs */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Tabs
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Rehab toolkit features"
                  sx={{
                    mb: 2,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 2,
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    },
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: 1.5,
                    },
                  }}
                >
                  <Tab label="Cognitive Tools" id="toolkit-tab-m-0" aria-controls="toolkit-tabpanel" />
                  <Tab label="Library" id="toolkit-tab-m-1" aria-controls="toolkit-tabpanel" />
                  <Tab label="Tracking" id="toolkit-tab-m-2" aria-controls="toolkit-tabpanel" />
                  <Tab label="Care Circle" id="toolkit-tab-m-3" aria-controls="toolkit-tabpanel" />
                </Tabs>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                  {tabValue === 0 &&
                    'Turn your rehab plan into interactive daily actions. Cognitive Tools combine reminders, nudges, trackers, journals and step-based tasks.'}
                  {tabValue === 1 &&
                    'Give every client access to structured, evidence-based strategies without starting from a blank page.'}
                  {tabValue === 2 &&
                    "See what's happening between sessions at a glance with clear visual trends and report-ready summaries."}
                  {tabValue === 3 &&
                    'Turn family and support workers into coordinated allies. The Care Circle brings carers into the same plan you see.'}
                </Typography>
              </Box>

              {/* Desktop: Vertical tabs */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Tabs
                  orientation="vertical"
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                  aria-label="Rehab toolkit features"
                  sx={{
                    '& .MuiTab-root': {
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      py: 3,
                      px: 3,
                      minHeight: 'auto',
                      borderRadius: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'white',
                        borderColor: 'primary.light',
                        transform: 'translateX(4px)',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'white',
                        boxShadow: 2,
                        borderColor: 'primary.main',
                        borderLeftWidth: 3,
                      },
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none',
                    },
                  }}
                >
                  <Tab
                    id="toolkit-tab-d-0"
                    aria-controls="toolkit-tabpanel"
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Cognitive Tools
                        </Typography>
                        {tabValue === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Turn your rehab plan into interactive daily actions. Cognitive Tools
                            combine reminders, nudges, trackers, journals and step-based tasks.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Tab
                    id="toolkit-tab-d-1"
                    aria-controls="toolkit-tabpanel"
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Library of Strategies & Education
                        </Typography>
                        {tabValue === 1 && (
                          <Typography variant="body2" color="text.secondary">
                            Give every client access to structured, evidence-based strategies
                            without starting from a blank page.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Tab
                    id="toolkit-tab-d-2"
                    aria-controls="toolkit-tabpanel"
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Outcome Tracking & Reports
                        </Typography>
                        {tabValue === 2 && (
                          <Typography variant="body2" color="text.secondary">
                            See what's happening between sessions at a glance with clear visual
                            trends and report-ready summaries.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Tab
                    id="toolkit-tab-d-3"
                    aria-controls="toolkit-tabpanel"
                    label={
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Family & Carer Involvement
                        </Typography>
                        {tabValue === 3 && (
                          <Typography variant="body2" color="text.secondary">
                            Turn family and support workers into coordinated allies. The Care
                            Circle brings carers into the same plan you see.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Tabs>
              </Box>
            </Box>
            <Box
              role="tabpanel"
              id="toolkit-tabpanel"
              aria-labelledby={`toolkit-tab-m-${tabValue} toolkit-tab-d-${tabValue}`}
              sx={{
                width: { xs: '100%', md: '60%' },
                bgcolor: 'white',
                borderRadius: 3,
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pb: { xs: 2, md: 4 },
                pr: { xs: 1, md: 2 },
              }}
            >
              {tabValue === 0 && <ScreenshotShowcase variant="compact" />}
              {tabValue === 1 && <LibraryMarquee />}
              {tabValue === 2 && (
                <Box
                  component="img"
                  src="/images/product/sarah/fatigue-chart-960.webp"
                  srcSet="/images/product/sarah/fatigue-chart-640.webp 640w, /images/product/sarah/fatigue-chart-960.webp 960w, /images/product/sarah/fatigue-chart-1440.webp 1440w"
                  sizes="(max-width: 900px) 100vw, 60vw"
                  alt="Fatigue tracker showing a client's boom-bust energy pattern over two weeks"
                  sx={{ width: '100%', height: 'auto' }}
                />
              )}
              {tabValue === 3 && <CareCircle />}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* The Care Cliff - Problem Section (moved after features) */}
      <Section variant="dark">
        <Grid container spacing={{ xs: 4, md: 8 }}>
          <Grid item xs={12} md={4}>
            <SectionHeader
              overline="The Challenge"
              title="The Care Cliff"
              sx={{ mb: 0 }}
              variant="dark"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography
              sx={{
                fontSize: '1.125rem',
                color: 'rgba(255,255,255,0.85)',
                mb: 4,
                lineHeight: 1.7,
              }}
            >
              Without daily reinforcement, progress made in sessions fades and clients struggle to follow through. Clinicians have
              limited face-to-face time, limited visibility into day-to-day struggles, and spend
              too much admin time chasing updates and evidencing outcomes.
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="h6"
                  sx={{ color: BrandColors.error.light, mb: 1, fontWeight: 600 }}
                >
                  Huge Gaps in Care
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  Gains made during therapy stall between sessions. Without everyday cues and
                  support, adherence drops and hard-won progress fades.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" sx={{ color: 'error.light', mb: 1, fontWeight: 600 }}>
                  Scattered Tools, Siloed Teams
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  Paper handouts, dozens of apps and messaging channels add complexity and makes
                  coordination and reporting a tedious manual time-drain.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Section>

      {/* Get Started for Free - Timeline Section */}
      <Section py={12}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <FadeIn direction="up">
              <SectionHeader
                overline="Low-risk trial → Real-world value"
                title="Get Started for Free"
                sx={{ mb: 3 }}
              />
            </FadeIn>
            <FadeIn direction="up" delay={100}>
              <Box
                sx={{
                  bgcolor: NeutralColors[50],
                  p: 3,
                  borderRadius: 2,
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  mb: 4,
                }}
              >
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1 }}>
                  "Very user friendly - set up goals, trackers, and invited my first client quickly."
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Lead Neuropsychologist
                </Typography>
              </Box>
            </FadeIn>
            <FadeIn direction="up" delay={200}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href={ExternalUrls.dashboardStart}
                  onClick={() =>
                    capture('cta_clicked', {
                      location: 'professionals_timeline',
                      cta_label: 'Create Free Account',
                      cta_href: ExternalUrls.dashboardStart,
                      cta_type: 'primary',
                      cta_external: true,
                    })
                  }
                >
                  Create Free Account
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/contact-us"
                  onClick={() =>
                    capture('cta_clicked', {
                      location: 'professionals_timeline',
                      cta_label: 'Book Demo',
                      cta_href: '/contact-us',
                      cta_type: 'secondary',
                      cta_external: false,
                    })
                  }
                >
                  Book Demo
                </Button>
              </Stack>
            </FadeIn>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ pl: { md: 4 } }}>
              {timelineSteps.map((step, index) => (
                <FadeIn key={index} direction="left" delay={index * 150}>
                  <Box
                    sx={{
                      display: 'flex',
                      mb: index < timelineSteps.length - 1 ? 4 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mr: 3,
                      }}
                    >
                      <NumberedStep number={index + 1} />
                      {index < timelineSteps.length - 1 && (
                        <Box
                          sx={{
                            width: 2,
                            flexGrow: 1,
                            bgcolor: 'divider',
                            mt: 1,
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ pt: 0.5 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                </FadeIn>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Section>

      {/* Evidence-Based & Recommended Section */}
      <Box sx={{ bgcolor: NeutralColors[50], py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <FadeIn direction="up">
            <SectionHeader
              overline="Built on research → Made for real life"
              title="Evidence-Based & Recommended"
              subtitle="Grounded in cognitive-rehab best practice, co-designed with clinicians and survivors, and informed by real-world use."
              centered
              sx={{ maxWidth: 700, mx: 'auto', mb: 6 }}
            />
          </FadeIn>

          <Grid container spacing={3} alignItems="stretch">
            {evidenceCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}>
                <FadeIn direction="up" delay={index * 100} sx={{ width: '100%', height: '100%' }}>
                  <InfoCard
                    title={card.title}
                    description={card.description}
                    icon={<FeatureIcon icon={card.icon} />}
                    cta={{
                      label: `${card.cta.label} →`,
                      href: card.cta.href,
                    }}
                  />
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <MetricsSection
        variant="dark"
        py={8}
        title="Turning therapy into daily action"
        metricIds={['promptsDelivered', 'journalEntries', 'educationModules']}
        alignMetrics="left"
        metricValueColor="white"
      />

      {/* Privacy Section */}
      <Section py={12}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <FadeIn direction="up">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <FeatureIcon
                  icon={<LockIcon />}
                  color={BrandColors.primary.main}
                  size="medium"
                  variant="square" // Existing had borderRadius: 2, matching 'square' (or default FeatureIcon radius? FeatureIcon square=2, circle=50%)
                // Existing was borderRadius: 2
                // FeatureIcon variant='circle' => 50%
                // FeatureIcon variant='square' => 2 -> matches
                />
                <Typography variant="h3">Privacy by design</Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '1.125rem',
                  color: 'text.secondary',
                  lineHeight: 1.7,
                }}
              >
                Security you can trust, transparency you can check - encrypted storage and
                transmission, and routine audits under a clinical safety framework. DTAC submission
                is underway.
              </Typography>
            </FadeIn>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              {privacyItems.map((item, index) => (
                <FadeIn key={index} direction="left" delay={index * 100}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      borderRadius: 1,
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        color: 'primary.main',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <Typography>{item}</Typography>
                  </Box>
                </FadeIn>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Section>

      {/* CTA Section */}
      <Section py={12}>
        <CallToActionCard
          location="professionals_bottom_cta"
          title="Ready to get started?"
          description="Join the community of clinicians and families using neumind today."
          primaryCTA={{ label: "Create Free Account", href: ExternalUrls.dashboardSignup, external: true }}
          secondaryCTA={{ label: "Contact Us", href: "/contact-us" }}
        />
      </Section>

      {/* Testimonials Section */}
      <Section py={10} variant="muted">
        <SectionHeader
          title="What Clinicians Say"
          centered
          sx={{ mb: 6 }}
        />
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <FadeIn direction="up">
            <TestimonialCarousel
              testimonials={Testimonials.filter(t => t.userType?.includes('professional'))}
              showLogos={true}
            />
          </FadeIn>
        </Box>
      </Section>

      {/* Case Study Section */}
      <Section py={10}>
        <SectionHeader
          title="Case Studies"
          subtitle="See how neumind is making a difference in clinical practice."
          centered
          sx={{ mb: 6 }}
        />
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <FadeIn direction="up">
            {(() => {
              const caseStudies = getResourcesByType('case-study');
              if (caseStudies.length > 0) {
                return <ResourceCard resource={caseStudies[0]} />;
              }
              return null;
            })()}
          </FadeIn>
        </Box>
      </Section>

      {/* FAQ Section */}
      <Section variant="default" py={8}>
        <SectionHeader
          title="FAQs"
          subtitle="Straight answers to the questions clinicians ask most."
          centered
          sx={{ mb: 8 }}
        />
        <FAQSection
          title="FAQs"
          faqs={faqs}
        />
      </Section>
    </>
  );
}
